import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Form, Button, Modal, Tabs, Tab, Row, Alert, InputGroup } from 'react-bootstrap';
import { COUNTRY_CHOICES, CURRENCY_CHOICES, UNITS_OF_MEASURE_CHOICES } from './choices';
import ReCAPTCHA from "react-google-recaptcha";

function SignupModal({ handleClose, show }) {
    const [usernameAvailable, setUsernameAvailable] = useState(true);
    const [emailAvailable, setEmailAvailable] = useState(true);
    const [formErrors, setFormErrors] = useState({});
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        first_name: '',
        date_of_birth: '',
        postal_code: '',
        city: '',
        country: '',
        preferred_language: 'en', // default to English
        preferred_currency: 'EUR', // default to INR
        preferred_unit: 'METRIC', // default to METRIC
        tnc: false // default to false
    });
    const [key, setKey] = useState('step1'); // Tab key to control the active tab

    // Signup success states
    const [signupSuccess, setSignupSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    // Recaptcha
    // reCAPTCHA state
    const [recaptchaToken, setRecaptchaToken] = useState(null);
    const recaptchaRef = React.createRef(); // Add a reference to the reCAPTCHA component


    const checkUsernameAvailability = async (username) => {
        if (username.length > 3) {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/users/check_username/?username=${username}`);
                setUsernameAvailable(!response.data.is_taken);
            } catch (error) {
                console.error('Error checking username availability', error);
            }
        }
    };

    const checkEmailAvailability = async (email) => {
        if (email.includes("@")) {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/users/check_email/?email=${email}`);
                setEmailAvailable(!response.data.is_taken);
            } catch (error) {
                console.error('Error checking email availability', error);
            }
        }
    };

    const validateForm = () => {
        let errors = {};

        // Step 1 validation
        if (key === 'step1') {
            if (!formData.first_name) errors.first_name = 'First name is required.';
            if (!formData.date_of_birth) {
                errors.date_of_birth = 'Date of birth is required.';
            } else {
                const dob = new Date(formData.date_of_birth);
                const today = new Date();
                let age = today.getFullYear() - dob.getFullYear();
                const monthDifference = today.getMonth() - dob.getMonth();

                if (
                    monthDifference < 0 ||
                    (monthDifference === 0 && today.getDate() < dob.getDate())
                ) {
                    age--;
                }

                if (age < 18) {
                    errors.date_of_birth = 'You must be at least 18 years old to sign up.';
                }
            }
            if (!formData.postal_code) errors.postal_code = 'Postal code is required.';
            if (!formData.city) errors.city = 'City is required.';
            if (!formData.country) errors.country = 'Country is required.';
        }

        // Step 2 validation (if needed)
        if (key === 'step3') {
            if (!formData.username) {
                errors.username = 'Username is required.';
            } else if (formData.username.length < 4) {
                errors.username = 'Username must be at least 4 characters long.';
            }
            if (!formData.email) {
                errors.email = 'Email is required.';
            } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
                errors.email = 'Email is invalid.';
            }
            if (!formData.password) {
                errors.password = 'Password is required.';
            } else if (formData.password.length < 8 || !/\d/.test(formData.password) || !/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
                errors.password = 'Password must be at least 8 characters long and include at least one number and one special character.';
            }
            if (!formData.tnc) {
                errors.tnc = 'Agreement to the terms and conditions is required.';
            }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            if (key === 'step3') {
                checkUsernameAvailability(formData.username);
                checkEmailAvailability(formData.email);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [formData.username, formData.email, key]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (type === "checkbox") {
            setFormData({ ...formData, [name]: checked });
        } else {
            if (name === "email" && emailAvailable === false) setEmailAvailable(true);
            if (name === "username" && usernameAvailable === false) setUsernameAvailable(true);

            setFormData({ ...formData, [name]: value });
        }
        setFormErrors({ ...formErrors, [name]: null });
    };


    const handleNextStep = () => {
        if (validateForm()) {
            if (key === 'step1') setKey('step2');
            if (key === 'step2') setKey('step3');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        // Ensure reCAPTCHA is completed
        if (!recaptchaToken) {
            alert("Please complete the reCAPTCHA to prove you are human.");
            return;
        }
        console.log(JSON.stringify(formData, null, 2));

        try {
            const response = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/users/register/`, formData);
            console.log('Signup Success', response.data);
            setFormData({
                username: '',
                email: '',
                password: '',
                first_name: '',
                date_of_birth: '',
                postal_code: '',
                city: '',
                country: '',
                preferred_language: 'en', // default to English
                preferred_currency: 'INR', // default to INR
                preferred_unit: 'METRIC', // default to METRIC
                tnc: false // default to false
            })
            // Set the success message and show it
            setSuccessMessage(response.data.message);
            setSignupSuccess(true);
        } catch (error) {
            console.error('Signup Failed', error.response.data);
        }
    };

    // Handle reCAPTCHA verification
    const handleRecaptchaChange = (token) => {
        setRecaptchaToken(token);
    };

    return (
        <Modal show={show} onHide={handleClose}>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {signupSuccess ? (
                        <Alert className="text-center mt-3">
                            <h4>{successMessage}</h4>
                            <p>Your account has been created successfully. You can now close this modal and sign in.</p>
                        </Alert>
                    ) : (
                        <Tabs activeKey={key} onSelect={(k) => setKey(k)}>
                            <Tab eventKey="step1" title="Basic Info">
                                <Row>
                                    <InputGroup className="mt-3">
                                        <InputGroup.Text id="basic-addon1" style={{ minWidth: '120px' }}>First Name</InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            name="first_name"
                                            value={formData.first_name}
                                            onChange={handleChange}
                                        />
                                    </InputGroup>
                                </Row>
                                <Row>
                                    <InputGroup className="mt-3">
                                        <InputGroup.Text style={{ minWidth: '120px' }}>Date of Birth</InputGroup.Text>
                                        <Form.Control
                                            type="date"
                                            name="date_of_birth"
                                            value={formData.date_of_birth}
                                            onChange={handleChange}
                                            isInvalid={formErrors.date_of_birth}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {formErrors.date_of_birth}
                                        </Form.Control.Feedback>
                                    </InputGroup>
                                </Row>
                                <Row>

                                    <InputGroup className="mt-3">
                                        <InputGroup.Text style={{ minWidth: '120px' }}>Postal Code</InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            name="postal_code"
                                            value={formData.postal_code}
                                            onChange={handleChange}
                                            isInvalid={formErrors.postal_code}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {formErrors.postal_code}
                                        </Form.Control.Feedback>
                                    </InputGroup>
                                </Row>
                                <Row>
                                    <InputGroup className="mt-3">
                                        <InputGroup.Text style={{ minWidth: '120px' }}>City</InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            name="city"
                                            value={formData.city}
                                            onChange={handleChange}
                                            isInvalid={formErrors.city}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {formErrors.city}
                                        </Form.Control.Feedback>
                                    </InputGroup>

                                </Row>
                                <Row>
                                    <InputGroup className="mt-3">
                                        <InputGroup.Text style={{ minWidth: '120px' }}>Country</InputGroup.Text>
                                        <Form.Select
                                            name="country"
                                            value={formData.country}
                                            onChange={handleChange}
                                            isInvalid={formErrors.country}
                                        >
                                            <option value="">Select Country</option>
                                            {COUNTRY_CHOICES.map((country) => (
                                                <option key={country.value} value={country.value}>
                                                    {country.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            {formErrors.country}
                                        </Form.Control.Feedback>
                                    </InputGroup>
                                </Row>
                                <Button variant="primary" onClick={handleNextStep} className="mt-3">
                                    Next
                                </Button>
                            </Tab>

                            <Tab eventKey="step2" title="Preferences" disabled={key !== 'step2'}>
                                <Row className="mt-3">
                                    <Alert variant="info">
                                        <i className="bi bi-info-circle-fill" style={{ marginRight: '5px' }}></i>
                                        Please choose carefully. These are not chanagable.
                                    </Alert>
                                </Row>
                                {/* 
                                <InputGroup className="mt-3">
                                    <InputGroup.Text style={{ minWidth: '180px' }}>Preferred Language</InputGroup.Text>
                                    <Form.Control
                                        as="select"
                                        name="preferred_language"
                                        value={formData.preferred_language}
                                        onChange={handleChange}
                                    >
                                        {LANGUAGE_CHOICES.map((language) => (
                                            <option key={language.value} value={language.value}>
                                                {language.label}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </InputGroup>
                                */}
                                <InputGroup className="mt-3">
                                    <InputGroup.Text style={{ minWidth: '180px' }}>Preferred Currency</InputGroup.Text>
                                    <Form.Control
                                        as="select"
                                        name="preferred_currency"
                                        value={formData.preferred_currency}
                                        onChange={handleChange}
                                        required
                                    >
                                        {CURRENCY_CHOICES.map((currency) => (
                                            <option key={currency.value} value={currency.value}>
                                                {currency.label}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </InputGroup>

                                <InputGroup className="mt-3">
                                    <InputGroup.Text style={{ minWidth: '180px' }}>Preferred Unit</InputGroup.Text>
                                    <Form.Control
                                        as="select"
                                        name="preferred_unit"
                                        value={formData.preferred_unit}
                                        onChange={handleChange}
                                        required
                                    >
                                        {UNITS_OF_MEASURE_CHOICES.map((unit) => (
                                            <option key={unit.value} value={unit.value}>
                                                {unit.label}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </InputGroup>
                                <Button variant="primary" onClick={handleNextStep} className="mt-3">
                                    Next
                                </Button>
                            </Tab>

                            <Tab eventKey="step3" title="Details" disabled={key !== 'step3'}>
                                <InputGroup className="mt-3">
                                    <InputGroup.Text style={{ minWidth: '120px' }}>Username</InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        name="username"
                                        value={formData.username}
                                        onChange={handleChange}
                                        isInvalid={!!formErrors.username || !usernameAvailable}
                                    />
                                    {formData.username.length > 0 && (
                                        <InputGroup.Text>
                                            {usernameAvailable ? (
                                                <i className="bi bi-check-circle-fill" style={{ color: 'green' }}></i>
                                            ) : (
                                                <i className="bi bi-x-circle-fill" style={{ color: 'red' }}></i>
                                            )}
                                        </InputGroup.Text>
                                    )}
                                    {formErrors.username && (
                                        <Form.Control.Feedback type="invalid">
                                            {formErrors.username}
                                        </Form.Control.Feedback>
                                    )}
                                    {!usernameAvailable && !formErrors.username && (
                                        <Form.Control.Feedback type="invalid">
                                            Username is taken.
                                        </Form.Control.Feedback>
                                    )}
                                </InputGroup>
                                <InputGroup className="mt-3">
                                    <InputGroup.Text style={{ minWidth: '120px' }}>Email</InputGroup.Text>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        isInvalid={formErrors.email || !emailAvailable}
                                    />
                                    {formData.email.length > 0 && (
                                        <InputGroup.Text>
                                            {emailAvailable ? (
                                                <i className="bi bi-check-circle-fill" style={{ color: 'green' }}></i>
                                            ) : (
                                                <i className="bi bi-x-circle-fill" style={{ color: 'red' }}></i>
                                            )
                                            }
                                        </InputGroup.Text>
                                    )}
                                    {formErrors.email && (
                                        <Form.Control.Feedback type="invalid">
                                            {formErrors.email}
                                        </Form.Control.Feedback>
                                    )}
                                    {!emailAvailable && !formErrors.email && (
                                        <Form.Control.Feedback type="invalid">
                                            Email is already registered.
                                        </Form.Control.Feedback>
                                    )}
                                </InputGroup>

                                <InputGroup className="mt-3">
                                    <InputGroup.Text style={{ minWidth: '120px' }}>Password</InputGroup.Text>
                                    <Form.Control
                                        type="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        isInvalid={formErrors.password}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formErrors.password}
                                    </Form.Control.Feedback>
                                </InputGroup>
                                <Row className="mt-3">
                                    <ReCAPTCHA
                                        ref={recaptchaRef}
                                        sitekey={`${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`}
                                        onChange={handleRecaptchaChange}
                                        badge="inline"
                                    />
                                </Row>
                                {/* Check box for terms and conditions */}
                                {/* Checkbox for terms and conditions */}
                                <Form.Check
                                    type="checkbox"
                                    name="tnc"
                                    label="I agree to the terms and conditions"
                                    checked={formData.tnc}
                                    onChange={handleChange}
                                    isInvalid={formErrors.tnc}
                                    id="terms-and-conditions"
                                    feedback={formErrors.tnc && formErrors.tnc}
                                    style={{ marginTop: '10px' }}
                                />
                                <Button variant="success" type="submit" className="mt-3">
                                    Sign Up
                                </Button>
                            </Tab>
                        </Tabs>
                    )}
                </Modal.Body>
            </Form>
        </Modal>
    );
}

export default SignupModal;
